import { Robot, type RobotRaw, type RobotTimeline } from "../types/Robot";
import { mergeRawPoints, replaceTimelineTail } from "./liveStreaming";

export interface LiveStreamState {
	robot: Robot | null;
	/** floor = dayStart on init; advances as raw ticks land (see `mergeRawPoints`). */
	watermarkMs: number;
	backfillDone: boolean;
	/** Wall-clock ms of the last merge that produced new data, or `null` before backfill completes. */
	lastDataAt: number | null;
}

export type LiveStreamAction =
	| { type: "BACKFILL_COMPLETE"; robot: Robot; floorMs: number; at: number }
	| { type: "RAW_TICK"; raw: RobotRaw[]; at: number }
	| { type: "TIMELINE_TICK"; timeline: RobotTimeline[]; fetchStartMs: number }
	| { type: "RESET"; floorMs: number };

export const createInitialLiveStreamState = (floorMs: number): LiveStreamState => ({
	robot: null,
	watermarkMs: floorMs,
	backfillDone: false,
	lastDataAt: null,
});

/** Pure reducer for a single robot's live-stream session. `RAW_TICK`/`TIMELINE_TICK` clone the
 *  robot via `Object.assign(new Robot(), state.robot, {...})` — NEVER object spread, which would
 *  drop the `Robot` class prototype and break `instanceof Robot` checks downstream (OpsMap's FC
 *  builders and other consumers rely on it). */
export const liveStreamReducer = (state: LiveStreamState, action: LiveStreamAction): LiveStreamState => {
	switch (action.type) {
		case "BACKFILL_COMPLETE": {
			const raw = action.robot.raw;
			const watermarkMs = raw && raw.length > 0 ? raw[raw.length - 1].timestamp : action.floorMs;
			return {
				robot: action.robot,
				watermarkMs,
				backfillDone: true,
				lastDataAt: action.at,
			};
		}

		case "RAW_TICK": {
			if (action.raw.length === 0) return state;
			if (!state.robot) return state;
			const { merged, watermarkMs } = mergeRawPoints(state.robot.raw ?? [], action.raw, state.watermarkMs);
			const robot = Object.assign(new Robot(), state.robot, { raw: merged });
			return { ...state, robot, watermarkMs, lastDataAt: action.at };
		}

		case "TIMELINE_TICK": {
			if (!state.robot) return state;
			const timeline = replaceTimelineTail(state.robot.timeline ?? [], action.timeline, action.fetchStartMs);
			const robot = Object.assign(new Robot(), state.robot, { timeline });
			return { ...state, robot };
		}

		case "RESET":
			return createInitialLiveStreamState(action.floorMs);

		default:
			return state;
	}
};
